import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Search, ArrowUpDown, Filter, Tv, CalendarDays, Clock, Heart, Shield, SlidersHorizontal } from "lucide-react";
import Button from "./ui/Button";
import Dropdown from "./ui/Dropdown";
import RadioGroup from "./ui/RadioGroup";
import ViewModeSwitcher from "./ViewModeSwitcher";
import DensityToggle from "./ui/DensityToggle";
import Typography from "./ui/Typography";
import Tooltip from "./ui/Tooltip";
import { cn } from "../utils/classNames";

const NewFilterBar = React.forwardRef(
    (
        {
            // The list being filtered
            totalCount = 0,

            // Search
            searchQuery = "",
            onSearchChange,

            // Sort
            sortBy = "releaseDate",
            onSortChange,

            // View mode
            viewMode = "grid",
            onViewModeChange,

            // Density
            density = "comfortable",
            onDensityChange,
            isMobile = false,

            // Filters
            selectedGenres = [],
            onGenresChange,
            availableGenres = [],

            selectedNetworks = [],
            onNetworksChange,
            availableNetworks = [],

            selectedContentRatings = [],
            onContentRatingsChange,
            availableContentRatings = [],

            // Series type
            seriesType = "all",
            onSeriesTypeChange,

            // Quick date filters
            quickDateRange = "all",
            onQuickDateRangeChange,
            watchlistOnly = false,
            onWatchlistToggle,
            watchlistCount = 0,
            filterBarId,

            className
        },
        ref
    ) => {
        const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
        const [showWatchlistTooltip, setShowWatchlistTooltip] = useState(false);
        const watchlistFilterRef = useRef(null);

        const sortOptions = [
            { value: "releaseDate", label: "Sort by release date" },
            { value: "rating", label: "Sort by rating" },
            { value: "title", label: "Sort by title" },
        ];

        // Find current sort label
        const currentSortLabel =
            sortOptions.find((option) => option.value === sortBy)?.label ||
            sortOptions[0].label;
        const advancedFilterCount = selectedGenres.length + selectedNetworks.length + selectedContentRatings.length + (seriesType !== "all" ? 1 : 0);
        const filterTitle = totalCount > 0 ? `Filter these ${totalCount} shows` : "Filter these shows";
        const searchPlaceholder = "Title, network or genre";
        const filterChipClassName = "filter-chip";

        return (
            <div
                id={filterBarId}
                className={cn(
                    "relative z-20 overflow-visible scroll-mt-24 sm:scroll-mt-28",
                    "space-y-3 bg-card/55 border border-border px-4 sm:px-5 py-4 rounded-lg",
                    className
                )}
                ref={ref}
            >
                <div className="flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-border/70">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-accent-taupe" />
                        <Typography
                            variant="h4"
                            as="h3"
                            className="text-foreground"
                        >
                            {filterTitle}
                        </Typography>
                    </div>
                    <Button type="button" variant="filter" size="sm" className="min-h-11 sm:hidden"
                        onClick={() => setMoreFiltersOpen((open) => !open)} aria-expanded={moreFiltersOpen}
                        aria-controls="quick-date-filters advanced-filters-panel">
                        {moreFiltersOpen ? "Hide filters" : `Filters${advancedFilterCount ? ` (${advancedFilterCount})` : ""}`}
                    </Button>
                    <span className="hidden text-body-small text-muted-foreground sm:inline">
                        Filter by date, genre, platform or format.
                    </span>
                </div>

                {/* Primary Filters */}
                <div className="flex flex-col lg:flex-row gap-3">
                    {/* Search Input */}
                    <div className="relative flex-1 min-w-0">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder={searchPlaceholder}
                            aria-label={`${filterTitle} by title, network or genre`}
                            value={searchQuery}
                            onChange={(e) => onSearchChange?.(e.target.value)}
                            className={cn(
                                "w-full pl-10 pr-4 py-3 border border-border rounded-md control-height leading-none",
                                "bg-background/65 text-foreground placeholder:text-muted-foreground text-button",
                                "focus:outline-hidden focus:ring-0 focus:border-primary focus:shadow-input-glow",
                                "transition-all duration-150"
                            )}
                        />
                    </div>

                    {/* Quick temporal filters */}
                    <div id="quick-date-filters" className={cn("grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center lg:flex-nowrap", !moreFiltersOpen && "hidden sm:flex")}>
                        <Button
                            type="button"
                            size="default"
                            variant={quickDateRange === "tonight" ? "filter-active" : "filter"}
                            onClick={() => onQuickDateRangeChange?.("tonight")}
                            className="control-height w-full sm:w-auto justify-center"
                        >
                            <Clock className="mr-2 h-4 w-4" aria-hidden="true" />
                            Tonight
                        </Button>
                        <Button
                            type="button"
                            size="default"
                            variant={quickDateRange === "week" ? "filter-active" : "filter"}
                            onClick={() => onQuickDateRangeChange?.("week")}
                            className="control-height w-full sm:w-auto justify-center"
                        >
                            <CalendarDays className="mr-2 h-4 w-4" aria-hidden="true" />
                            This week
                        </Button>
                        <div
                            ref={watchlistFilterRef}
                            className="relative col-span-2 sm:col-auto"
                            onMouseEnter={() => watchlistCount === 0 && setShowWatchlistTooltip(true)}
                            onMouseLeave={() => setShowWatchlistTooltip(false)}
                            onFocus={() => watchlistCount === 0 && setShowWatchlistTooltip(true)}
                            onBlur={() => setShowWatchlistTooltip(false)}
                        >
                            <Button
                                type="button"
                                size="default"
                                variant={watchlistOnly ? "filter-active" : "filter"}
                                onClick={() => onWatchlistToggle?.()}
                                disabled={watchlistCount === 0}
                                className={cn(
                                    "control-height w-full sm:w-auto justify-center",
                                    watchlistCount === 0 && "opacity-40 cursor-not-allowed"
                                )}
                            >
                                <Heart className="mr-2 h-4 w-4" aria-hidden="true" />
                                My List{watchlistCount ? ` (${watchlistCount})` : ""}
                            </Button>
                            {showWatchlistTooltip && (
                                <Tooltip
                                    position="bottom"
                                    className="px-3 py-2"
                                    contentClassName="max-w-[220px] text-caption"
                                    anchorRef={watchlistFilterRef}
                                    onClose={() => setShowWatchlistTooltip(false)}
                                >
                                    Save a show to unlock the My List filter.
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    {/* Sort stays in view, not behind More filters */}
                    <div className="w-full lg:w-52 lg:shrink-0">
                            <Dropdown
                                value={[currentSortLabel]}
                                onChange={(values) => {
                                    const selectedLabel = values[0];
                                    const selectedOption = sortOptions.find(
                                        (option) => option.label === selectedLabel
                                    );
                                    if (selectedOption) {
                                        onSortChange?.(selectedOption.value);
                                    }
                                }}
                                options={sortOptions.map((option) => option.label)}
                                placeholder="Sort by"
                                multiple={false}
                                icon={
                                    <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                                }
                            />
                    </div>
                </div>

                <div className="hidden flex-wrap items-center justify-between gap-3 sm:flex">
                    <Button
                        type="button"
                        variant="filter"
                        size="default"
                        onClick={() => setMoreFiltersOpen((prev) => !prev)}
                        className="w-full control-height justify-center sm:w-auto"
                        aria-expanded={moreFiltersOpen}
                        aria-controls="advanced-filters-panel"
                    >
                        <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />
                        {moreFiltersOpen ? "Hide filters" : `More filters${advancedFilterCount ? ` (${advancedFilterCount})` : ""}`}
                    </Button>

                    {onViewModeChange && (
                        <div className="flex items-center gap-2">
                            <span className="hidden text-body-small text-muted-foreground sm:inline">
                                View
                            </span>
                            <ViewModeSwitcher
                                viewMode={viewMode}
                                onChange={onViewModeChange}
                            />
                            {onDensityChange && viewMode !== "compact" && (
                                <DensityToggle
                                    density={density}
                                    onChange={onDensityChange}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* Advanced Filters + Display Controls */}
                <div
                    id="advanced-filters-panel"
                    className={cn(
                        "rounded-md border border-border/70 bg-background/35 p-3",
                        "flex-col lg:flex-row gap-3 items-start lg:items-center",
                        moreFiltersOpen ? "flex" : "hidden"
                    )}
                >
                    <div className="flex flex-col sm:flex-row gap-3 flex-1 min-w-0">
                        {/* Genre Filter */}
                        {availableGenres.length > 0 && (
                            <div className="flex-1 min-w-0">
                                <Dropdown
                                    value={selectedGenres}
                                    onChange={onGenresChange}
                                    options={availableGenres}
                                    placeholder="All genres"
                                    multiple={true}
                                    icon={
                                        <Filter className="h-4 w-4 text-muted-foreground" />
                                    }
                                />
                            </div>
                        )}

                        {/* Network Filter */}
                        {availableNetworks.length > 0 && (
                            <div className="flex-1 min-w-0">
                                <Dropdown
                                    value={selectedNetworks}
                                    onChange={onNetworksChange}
                                    options={availableNetworks}
                                    placeholder="All networks"
                                    multiple={true}
                                    icon={
                                        <Tv className="h-4 w-4 text-muted-foreground" />
                                    }
                                />
                            </div>
                        )}

                        {availableContentRatings.length > 0 && (
                            <div className="flex-1 min-w-0">
                                <Dropdown
                                    value={selectedContentRatings}
                                    onChange={onContentRatingsChange}
                                    options={availableContentRatings}
                                    placeholder="All ratings"
                                    multiple={true}
                                    icon={
                                        <Shield className="h-4 w-4 text-muted-foreground" />
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                {moreFiltersOpen && (
                    <RadioGroup
                        name="series-type"
                        value={seriesType}
                        onChange={onSeriesTypeChange}
                        direction="horizontal"
                        size={isMobile ? "sm" : "md"}
                        className="gap-x-3 gap-y-2 rounded-md border border-border/70 bg-background/35 p-3"
                        aria-label="Series type"
                    >
                        <RadioGroup.Option value="all">
                            All series
                        </RadioGroup.Option>
                        <RadioGroup.Option value="new">
                            New series only
                        </RadioGroup.Option>
                        <RadioGroup.Option value="returning">
                            Returning seasons only
                        </RadioGroup.Option>
                    </RadioGroup>
                )}

                {/* Active Filters Summary - Optional */}
                {(selectedGenres.length > 0 ||
                    selectedNetworks.length > 0 ||
                    selectedContentRatings.length > 0 ||
                    searchQuery ||
                    seriesType !== "all" ||
                    quickDateRange !== "all" ||
                    watchlistOnly) && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-border/70">
                        <span className="text-body-xs text-muted-foreground text-label">
                            Showing:
                        </span>

                        {searchQuery && (
                            <Button
                                variant="filter"
                                size="sm"
                                onClick={() => onSearchChange?.("")}
                                className={filterChipClassName}
                            >
                                Search: &quot;{searchQuery}&quot; ×
                            </Button>
                        )}

                        {selectedGenres.map((genre) => (
                            <Button
                                key={`genre-${genre}`}
                                variant="filter"
                                size="sm"
                                onClick={() => {
                                    const newGenres = selectedGenres.filter(
                                        (g) => g !== genre
                                    );
                                    onGenresChange?.(newGenres);
                                }}
                                className={filterChipClassName}
                            >
                                {genre} ×
                            </Button>
                        ))}

                        {selectedNetworks.map((network) => (
                            <Button
                                key={`network-${network}`}
                                variant="filter"
                                size="sm"
                                onClick={() => {
                                    const newNetworks = selectedNetworks.filter(
                                        (n) => n !== network
                                    );
                                    onNetworksChange?.(newNetworks);
                                }}
                                className={filterChipClassName}
                            >
                                {network} ×
                            </Button>
                        ))}

                        {selectedContentRatings.map((rating) => (
                            <Button
                                key={`content-rating-${rating}`}
                                variant="filter"
                                size="sm"
                                onClick={() => {
                                    const newRatings = selectedContentRatings.filter(
                                        (r) => r !== rating
                                    );
                                    onContentRatingsChange?.(newRatings);
                                }}
                                className={filterChipClassName}
                            >
                                {rating} ×
                            </Button>
                        ))}

                        {seriesType === "new" && (
                            <Button
                                variant="filter"
                                size="sm"
                                onClick={() => onSeriesTypeChange?.("all")}
                                className={filterChipClassName}
                            >
                                New series only ×
                            </Button>
                        )}

                        {seriesType === "returning" && (
                            <Button
                                variant="filter"
                                size="sm"
                                onClick={() => onSeriesTypeChange?.("all")}
                                className={filterChipClassName}
                            >
                                Returning seasons only ×
                            </Button>
                        )}

                        {quickDateRange !== "all" && (
                            <Button
                                variant="filter"
                                size="sm"
                                onClick={() => onQuickDateRangeChange?.("all")}
                                className={filterChipClassName}
                            >
                                {quickDateRange === "tonight" ? "Tonight" : "This week"} ×
                            </Button>
                        )}

                        {watchlistOnly && (
                            <Button
                                variant="filter"
                                size="sm"
                                onClick={() => onWatchlistToggle?.()}
                                className={filterChipClassName}
                            >
                                My List ×
                            </Button>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

NewFilterBar.displayName = "NewFilterBar";

NewFilterBar.propTypes = {
    totalCount: PropTypes.number,

    // Search
    searchQuery: PropTypes.string,
    onSearchChange: PropTypes.func,

    // Sort
    sortBy: PropTypes.string,
    onSortChange: PropTypes.func,

    // View mode
    viewMode: PropTypes.oneOf(["grid", "compact", "list"]),
    onViewModeChange: PropTypes.func,

    // Density
    density: PropTypes.oneOf(["comfortable", "compact"]),
    onDensityChange: PropTypes.func,
    isMobile: PropTypes.bool,

    // Filters
    selectedGenres: PropTypes.arrayOf(PropTypes.string),
    onGenresChange: PropTypes.func,
    availableGenres: PropTypes.arrayOf(PropTypes.string),

    selectedNetworks: PropTypes.arrayOf(PropTypes.string),
    onNetworksChange: PropTypes.func,
    availableNetworks: PropTypes.arrayOf(PropTypes.string),

    selectedContentRatings: PropTypes.arrayOf(PropTypes.string),
    onContentRatingsChange: PropTypes.func,
    availableContentRatings: PropTypes.arrayOf(PropTypes.string),

    // Series type
    seriesType: PropTypes.oneOf(["all", "new", "returning"]),
    onSeriesTypeChange: PropTypes.func,

    // Quick date filters
    quickDateRange: PropTypes.oneOf(["all", "tonight", "week"]),
    onQuickDateRangeChange: PropTypes.func,
    watchlistOnly: PropTypes.bool,
    onWatchlistToggle: PropTypes.func,
    watchlistCount: PropTypes.number,
    filterBarId: PropTypes.string,

    className: PropTypes.string,
};

export default NewFilterBar;
